import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Plus, Trash2, Check, RotateCcw, Undo2, Pencil, GripVertical, Upload, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { getUserSettings } from "@/lib/userSettings";
import { speak } from "@/lib/ttsService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MainLayout } from "@/components/layout/MainLayout";
import { useTranslation } from "@/hooks/useTranslation";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { EmojiPicker } from "@/components/ui/EmojiPicker";
import { LucideIconPicker } from "@/components/ui/LucideIconPicker";
import { icons } from "lucide-react";

type IconMode = "emoji" | "lucide";

interface EmergencyItem {
  id: string;
  labelMale: string;
  labelFemale: string;
  labelMaleEn?: string;
  labelFemaleEn?: string;
  labelMaleRu?: string;
  labelFemaleRu?: string;
  labelMaleAr?: string;
  labelFemaleAr?: string;
  emoji: string;
  iconName?: string; // For Lucide icon mode
  iconMode?: IconMode; // Which mode is used
}

const DEFAULT_EMERGENCY_ITEMS: EmergencyItem[] = [
  { id: "pain", labelMale: "כואב לי", labelFemale: "כואב לי", labelMaleEn: "I'm in pain", labelFemaleEn: "I'm in pain", labelMaleRu: "У меня болит", labelFemaleRu: "У меня болит", labelMaleAr: "أشعر بالألم", labelFemaleAr: "أشعر بالألم", emoji: "😣" },
  { id: "call_caregiver", labelMale: "לקרוא למטפל", labelFemale: "לקרוא למטפל", labelMaleEn: "Call caregiver", labelFemaleEn: "Call caregiver", labelMaleRu: "Позвать опекуна", labelFemaleRu: "Позвать опекуна", labelMaleAr: "اتصل بمقدم الرعاية", labelFemaleAr: "اتصل بمقدم الرعاية", emoji: "🔔" },
  { id: "breathing", labelMale: "קשה לי לנשום", labelFemale: "קשה לי לנשום", labelMaleEn: "Hard to breathe", labelFemaleEn: "Hard to breathe", labelMaleRu: "Трудно дышать", labelFemaleRu: "Трудно дышать", labelMaleAr: "صعوبة في التنفس", labelFemaleAr: "صعوبة في التنفس", emoji: "😰" },
  { id: "not_well", labelMale: "אני מרגיש לא טוב", labelFemale: "אני מרגישה לא טוב", labelMaleEn: "I don't feel well", labelFemaleEn: "I don't feel well", labelMaleRu: "Мне плохо", labelFemaleRu: "Мне плохо", labelMaleAr: "لا أشعر بخير", labelFemaleAr: "لا أشعر بخير", emoji: "🤒" },
  { id: "bathroom", labelMale: "אני צריך לשירותים", labelFemale: "אני צריכה לשירותים", labelMaleEn: "I need to use the bathroom", labelFemaleEn: "I need to use the bathroom", labelMaleRu: "Туалет", labelFemaleRu: "Туалет", labelMaleAr: "الحمام", labelFemaleAr: "الحمام", emoji: "🚽" },
  { id: "thirsty", labelMale: "אני רוצה לשתות", labelFemale: "אני רוצה לשתות", labelMaleEn: "I want to drink", labelFemaleEn: "I want to drink", labelMaleRu: "Хочу пить", labelFemaleRu: "Хочу пить", labelMaleAr: "أريد أن أشرب", labelFemaleAr: "أريد أن أشرب", emoji: "💧" },
  { id: "suction", labelMale: "אני צריך סאקשן", labelFemale: "אני צריכה סאקשן", labelMaleEn: "I need suction", labelFemaleEn: "I need suction", labelMaleRu: "Нужна аспирация", labelFemaleRu: "Нужна аспирация", labelMaleAr: "بحاجة شفط", labelFemaleAr: "بحاجة شفط", emoji: "💨" },
  { id: "position", labelMale: "אני צריך שינוי תנוחה", labelFemale: "אני צריכה שינוי תנוחה", labelMaleEn: "I need a position change", labelFemaleEn: "I need a position change", labelMaleRu: "Сменить положение", labelFemaleRu: "Сменить положение", labelMaleAr: "تغيير الوضعية", labelFemaleAr: "تغيير الوضعية", emoji: "🔄" },
];

const STORAGE_KEY = "emergencyBoardItems_v9"; // v9: added RU/AR translations
const OLD_STORAGE_KEYS = ["emergencyBoardItems_v8", "emergencyBoardItems_v7", "emergencyBoardItems_v6", "emergencyBoardItems_v5"];
// Merge Russian/Arabic labels from defaults into saved items (preserves user customizations)
function migrateItems(saved: EmergencyItem[]): EmergencyItem[] {
  return saved.map(item => {
    const defaultItem = DEFAULT_EMERGENCY_ITEMS.find(d => d.id === item.id);
    if (defaultItem) {
      return {
        ...item,
        labelMaleRu: item.labelMaleRu || defaultItem.labelMaleRu,
        labelFemaleRu: item.labelFemaleRu || defaultItem.labelFemaleRu,
        labelMaleAr: item.labelMaleAr || defaultItem.labelMaleAr,
        labelFemaleAr: item.labelFemaleAr || defaultItem.labelFemaleAr,
      };
    }
    return item;
  });
}
const MAX_UNDO_HISTORY = 20;
const ITEMS_PER_PAGE = 8; // 2 rows × 4 columns

export default function Emergency() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const settings = getUserSettings();
  const isFemale = settings.gender === "female";
  const { dir, t: tUser, lang } = useTranslation();
  const isHebrew = lang === "he";
  const { t: tCg, isRTL: isCgRTL, dir: cgDir } = useCaregiverTranslation();
  const { user } = useAuth();
  
  // Check if coming from Help screen
  const fromHelp = location.state?.fromHelp === true;
  
  // Check if in edit mode from URL parameter (accessed from hamburger menu)
  const isEditModeFromUrl = searchParams.get('edit') === 'true';
  
  const [items, setItems] = useState<EmergencyItem[]>(() => {
    // Try current version first
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return migrateItems(JSON.parse(saved));
    // Migrate from old versions
    for (const oldKey of OLD_STORAGE_KEYS) {
      const oldSaved = localStorage.getItem(oldKey);
      if (oldSaved) {
        const migrated = migrateItems(JSON.parse(oldSaved));
        localStorage.removeItem(oldKey);
        return migrated;
      }
    }
    return DEFAULT_EMERGENCY_ITEMS;
  });
  
  // Undo history - stores previous states
  const [undoHistory, setUndoHistory] = useState<EmergencyItem[][]>([]);
  const isUndoing = useRef(false);
  
  // Drag state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  
  // File upload ref for custom images
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setNewItemForm(prev => ({ ...prev, emoji: base64, iconMode: "emoji" }));
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };
  
  const [isEditMode, setIsEditMode] = useState(isEditModeFromUrl);
  const [editingItem, setEditingItem] = useState<EmergencyItem | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [newItemForm, setNewItemForm] = useState({
    labelMale: "",
    labelFemale: "",
    labelMaleEn: "",
    labelFemaleEn: "",
    labelMaleRu: "",
    labelFemaleRu: "",
    labelMaleAr: "",
    labelFemaleAr: "",
    emoji: "❗",
    iconName: "AlertCircle",
    iconMode: "emoji" as IconMode,
    showVisual: true,
  });

  // Get the male/female field keys for the current user language
  const getLangMaleKey = (): keyof EmergencyItem => {
    if (lang === "ru") return "labelMaleRu";
    if (lang === "ar") return "labelMaleAr";
    if (lang === "en") return "labelMaleEn";
    return "labelMale";
  };
  const getLangFemaleKey = (): keyof EmergencyItem => {
    if (lang === "ru") return "labelFemaleRu";
    if (lang === "ar") return "labelFemaleAr";
    if (lang === "en") return "labelFemaleEn";
    return "labelFemale";
  };
  const getLangFormMaleKey = () => {
    if (lang === "ru") return "labelMaleRu" as const;
    if (lang === "ar") return "labelMaleAr" as const;
    if (lang === "en") return "labelMaleEn" as const;
    return "labelMale" as const;
  };
  const getLangFormFemaleKey = () => {
    if (lang === "ru") return "labelFemaleRu" as const;
    if (lang === "ar") return "labelFemaleAr" as const;
    if (lang === "en") return "labelFemaleEn" as const;
    return "labelFemale" as const;
  };

  const getItemLabel = (item: EmergencyItem) => {
    if (lang === "he") {
      return isFemale ? item.labelFemale : item.labelMale;
    }
    if (lang === "ru") {
      return isFemale ? (item.labelFemaleRu || item.labelFemaleEn || item.labelFemale) : (item.labelMaleRu || item.labelMaleEn || item.labelMale);
    }
    if (lang === "ar") {
      return isFemale ? (item.labelFemaleAr || item.labelFemaleEn || item.labelFemale) : (item.labelMaleAr || item.labelMaleEn || item.labelMale);
    }
    return isFemale ? (item.labelFemaleEn || item.labelFemale) : (item.labelMaleEn || item.labelMale);
  };

  const cols = 4;
  const rows = 2; // Fixed 2 rows per page

  // Pagination calculations
  const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);
  const hasMultiplePages = totalPages > 1;
  
  // Get items for current page
  const paginatedItems = useMemo(() => {
    const startIndex = currentPage * ITEMS_PER_PAGE;
    return items.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [items, currentPage]);

  // Ensure current page is valid when items change
  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(totalPages - 1);
    }
  }, [items.length, currentPage, totalPages]);

  const handlePrevPage = useCallback(() => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  }, [totalPages]);

  // Match Communication Board pagination semantics:
  // In RTL: left arrow = next page, right arrow = previous page
  // In LTR: left arrow = previous page, right arrow = next page
  const hasPrevPage = currentPage > 0;
  const hasNextPage = currentPage < totalPages - 1;
  const showLeftArrow = isHebrew ? hasNextPage : hasPrevPage;
  const showRightArrow = isHebrew ? hasPrevPage : hasNextPage;

  const handleLeftArrowClick = useCallback(() => {
    if (isHebrew) {
      handleNextPage();
    } else {
      handlePrevPage();
    }
  }, [handleNextPage, handlePrevPage, isHebrew]);

  const handleRightArrowClick = useCallback(() => {
    if (isHebrew) {
      handlePrevPage();
    } else {
      handleNextPage();
    }
  }, [handleNextPage, handlePrevPage, isHebrew]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const sendEmergencyPush = async (message: string) => {
    if (!user) return;
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      await supabase.functions.invoke('send-push-alert', {
        body: {
          patientId: user.id,
          alertType: 'emergency_detail',
          message: message,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
    } catch (error) {
      // Silent fail
    }
  };

  const handleItemClick = (item: EmergencyItem) => {
    if (isEditMode) {
      setEditingItem(item);
      setNewItemForm({
        labelMale: item.labelMale,
        labelFemale: item.labelFemale,
        labelMaleEn: item.labelMaleEn || "",
        labelFemaleEn: item.labelFemaleEn || "",
        labelMaleRu: item.labelMaleRu || "",
        labelFemaleRu: item.labelFemaleRu || "",
        labelMaleAr: item.labelMaleAr || "",
        labelFemaleAr: item.labelFemaleAr || "",
        emoji: item.emoji,
        iconName: item.iconName || "AlertCircle",
        iconMode: item.iconMode || (item.iconName ? "lucide" : "emoji"),
        showVisual: true,
      });
      setShowEditForm(true);
      return;
    }

    const label = getItemLabel(item);
    speak({
      text: label,
      gender: settings.voiceGender || "female",
      neutral: true,
    });

    const userName = settings.name || (isHebrew ? "המשתמש" : "User");
    const pushMessage = isHebrew 
      ? `${userName} בחר/ה: ${item.emoji} ${label}`
      : `${userName} selected: ${item.emoji} ${label}`;
    sendEmergencyPush(pushMessage);
    
    // If coming from Help screen, navigate back with the selected message
    if (fromHelp) {
      navigate("/help", { 
        state: {
          selectedMessage: label,
          selectedEmoji: item.emoji,
          selectedIconName: item.iconName,
          selectedIconMode: item.iconMode,
        },
        replace: true 
      });
    }
  };

  const handleAddItem = () => {
    setEditingItem(null);
    setNewItemForm({ labelMale: "", labelFemale: "", labelMaleEn: "", labelFemaleEn: "", labelMaleRu: "", labelFemaleRu: "", labelMaleAr: "", labelFemaleAr: "", emoji: "❗", iconName: "AlertCircle", iconMode: "emoji", showVisual: true });
    setShowEditForm(true);
  };

  // Save state to undo history before making changes
  const saveToHistory = useCallback((currentItems: EmergencyItem[]) => {
    setUndoHistory(prev => {
      const newHistory = [...prev, currentItems];
      // Limit history size
      if (newHistory.length > MAX_UNDO_HISTORY) {
        return newHistory.slice(-MAX_UNDO_HISTORY);
      }
      return newHistory;
    });
  }, []);

  // Undo last action
  const handleUndo = useCallback(() => {
    if (undoHistory.length === 0) return;
    
    isUndoing.current = true;
    const previousState = undoHistory[undoHistory.length - 1];
    setUndoHistory(prev => prev.slice(0, -1));
    setItems(previousState);
    
    // Reset the flag after state update
    setTimeout(() => {
      isUndoing.current = false;
    }, 0);
  }, [undoHistory]);

  const handleSaveItem = () => {
    const maleKey = getLangFormMaleKey();
    const femaleKey = getLangFormFemaleKey();
    if (!newItemForm[maleKey].trim()) return;

    // Save current state to history before modifying
    saveToHistory(items);

    if (editingItem) {
      setItems(items.map(item => 
        item.id === editingItem.id 
          ? { 
              ...item, 
              labelMale: newItemForm.labelMale,
              labelFemale: newItemForm.labelFemale || newItemForm.labelMale,
              labelMaleEn: newItemForm.labelMaleEn || newItemForm.labelMale,
              labelFemaleEn: newItemForm.labelFemaleEn || newItemForm.labelFemale || newItemForm.labelMale,
              labelMaleRu: newItemForm.labelMaleRu,
              labelFemaleRu: newItemForm.labelFemaleRu,
              labelMaleAr: newItemForm.labelMaleAr,
              labelFemaleAr: newItemForm.labelFemaleAr,
              emoji: newItemForm.iconMode === "emoji" ? newItemForm.emoji : "",
              iconName: newItemForm.iconMode === "lucide" ? newItemForm.iconName : undefined,
              iconMode: newItemForm.iconMode,
            }
          : item
      ));
    } else {
      const newItem: EmergencyItem = {
        id: `custom-${Date.now()}`,
        labelMale: newItemForm.labelMale,
        labelFemale: newItemForm.labelFemale || newItemForm.labelMale,
        labelMaleEn: newItemForm.labelMaleEn || newItemForm.labelMale,
        labelFemaleEn: newItemForm.labelFemaleEn || newItemForm.labelFemale || newItemForm.labelMale,
        labelMaleRu: newItemForm.labelMaleRu,
        labelFemaleRu: newItemForm.labelFemaleRu,
        labelMaleAr: newItemForm.labelMaleAr,
        labelFemaleAr: newItemForm.labelFemaleAr,
        emoji: newItemForm.iconMode === "emoji" ? newItemForm.emoji : "",
        iconName: newItemForm.iconMode === "lucide" ? newItemForm.iconName : undefined,
        iconMode: newItemForm.iconMode,
      };
      setItems([...items, newItem]);
    }
    
    setShowEditForm(false);
    setEditingItem(null);
  };

  const handleDeleteItem = (id: string) => {
    // Save current state to history before deleting
    saveToHistory(items);
    setItems(items.filter(item => item.id !== id));
  };

  const handleResetToDefault = () => {
    // Save current state to history before reset
    saveToHistory(items);
    setItems(DEFAULT_EMERGENCY_ITEMS);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    saveToHistory(items);
    const newItems = [...items];
    const [moved] = newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, moved);
    setItems(newItems);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleBack = useCallback(() => {
    // If in edit form, close it first
    if (showEditForm) {
      setShowEditForm(false);
      setEditingItem(null);
      return;
    }
    // Otherwise go back in browser history
    navigate(-1);
  }, [showEditForm, navigate]);

  const VISUAL_SIZE = 'calc(clamp(2.5rem, 8vh, 6rem) * var(--font-scale, 1))';
  const LABEL_SIZE = 'calc(clamp(1em, 3vh, 2em) * var(--font-scale, 1))';

  if (showEditForm) {
    return (
      <MainLayout onBack={() => setShowEditForm(false)}>
        <div className="flex-1 flex flex-col p-6 overflow-auto bg-white dark:bg-transparent" dir={cgDir}>
          <h1 className="text-2xl font-bold text-foreground mb-6">
            {editingItem ? tCg("editItem") : tCg("addNewItem")}
          </h1>

          <div className="max-w-lg mx-auto w-full space-y-6">
            <div>
              <label className="block text-lg font-medium mb-2">{tCg("textMale")}</label>
              <Input
                value={newItemForm[getLangFormMaleKey()]}
                onChange={(e) => setNewItemForm({ ...newItemForm, [getLangFormMaleKey()]: e.target.value })}
                placeholder={tCg("textMalePlaceholder")}
                className="h-14 text-lg"
                dir={dir}
              />
            </div>
            
            <div>
              <label className="block text-lg font-medium mb-2">{tCg("textFemale")}</label>
              <Input
                value={newItemForm[getLangFormFemaleKey()]}
                onChange={(e) => setNewItemForm({ ...newItemForm, [getLangFormFemaleKey()]: e.target.value })}
                placeholder={tCg("textFemalePlaceholder")}
                className="h-14 text-lg"
                dir={dir}
              />
            </div>
            
            {/* Visual Element Section */}
            <div className="space-y-3">
              <label className="block text-lg font-medium">
                {isCgRTL ? "אלמנט ויזואלי" : "Visual Element"}
              </label>
              
              {/* Show/Hide Visual Element Toggle */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-xl opacity-50">
                      <label className="text-base text-muted-foreground">
                        {isCgRTL ? "הצג אלמנט ויזואלי" : "Show Visual Element"}
                      </label>
                      <Switch
                        checked={newItemForm.showVisual}
                        disabled
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{isCgRTL ? "אפשרות זו אינה זמינה כרגע" : "This option is not available yet"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Visual Element Picker - only show if showVisual is enabled */}
              {newItemForm.showVisual && (() => {
                const hasCustomImage = newItemForm.emoji.startsWith("data:image");
                return (
                <div className="space-y-4">
                  {/* Hidden file input for custom image upload */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />

                  {/* Custom image preview if uploaded */}
                  {hasCustomImage && (
                    <div className="flex items-center justify-center p-4 bg-muted rounded-xl">
                      <img src={newItemForm.emoji} alt="Custom" className="w-16 h-16 object-contain rounded-lg" />
                    </div>
                  )}

                  {/* Upload Image button - always visible above tabs */}
                  <div className="flex justify-center">
                    <Button
                      variant="outline"
                      onClick={triggerFileUpload}
                      className="gap-2"
                    >
                      <Upload className="w-5 h-5" />
                      {isCgRTL ? "העלאת תמונה" : "Upload Image"}
                    </Button>
                  </div>

                  {/* Type selector: Emoji / Icon */}
                  <div className="flex items-center justify-center gap-2 p-2 bg-muted/30 rounded-lg">
                    <button
                      onClick={() => setNewItemForm({ ...newItemForm, iconMode: "emoji", emoji: hasCustomImage ? "" : newItemForm.emoji })}
                      className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                        !hasCustomImage && newItemForm.iconMode === "emoji"
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "bg-background hover:bg-accent"
                      }`}
                    >
                      {isCgRTL ? "אימוג'י" : "Emoji"}
                    </button>
                    <button
                      onClick={() => setNewItemForm({ ...newItemForm, iconMode: "lucide", emoji: hasCustomImage ? "" : newItemForm.emoji })}
                      className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                        !hasCustomImage && newItemForm.iconMode === "lucide"
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "bg-background hover:bg-accent"
                      }`}
                    >
                      {isCgRTL ? "אייקון" : "Icon"}
                    </button>
                  </div>

                  {/* Picker - hidden when custom image is active */}
                  {!hasCustomImage && (
                    newItemForm.iconMode === "lucide" ? (
                      <LucideIconPicker
                        selectedIcon={newItemForm.iconName}
                        onIconSelect={(iconName) => setNewItemForm({ ...newItemForm, iconName })}
                        isHebrew={isCgRTL}
                      />
                    ) : (
                      <EmojiPicker
                        selectedEmoji={newItemForm.emoji}
                        onEmojiSelect={(emoji) => setNewItemForm({ ...newItemForm, emoji })}
                        isHebrew={isCgRTL}
                      />
                    )
                  )}
                </div>
                );
              })()}
            </div>
            
            <div className="flex gap-4 pt-6">
              <Button onClick={handleSaveItem} className="flex-1 h-16 text-xl">
                <Check className="w-6 h-6 mr-2" />
                {tCg("save")}
              </Button>
              <Button variant="outline" onClick={() => setShowEditForm(false)} className="h-16 px-8 text-lg">
                {tCg("cancel")}
              </Button>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      onBack={handleBack}
    >
      <div className="flex-1 flex flex-col p-6 overflow-hidden" dir={dir}>
        {/* Title and Edit Controls - only show if in edit mode from hamburger */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-foreground">
            {isEditMode ? (isCgRTL ? "עריכת לוח עזרה" : "Edit Help Board") : tUser("whatHappened")}
          </h1>
          
          {isEditMode && (
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="lg" 
                onClick={handleUndo}
                disabled={undoHistory.length === 0}
                className="disabled:opacity-50"
              >
                <Undo2 className="w-5 h-5 mr-2" />
                {tCg("undo")}
              </Button>
              
              <Button variant="outline" size="lg" onClick={handleAddItem}>
                <Plus className="w-5 h-5 mr-2" />
                {tCg("addCard")}
              </Button>
              
              <Button variant="outline" size="lg" onClick={handleResetToDefault}>
                <RotateCcw className="w-5 h-5 mr-2" />
                {tCg("resetToDefault")}
              </Button>
              
              <Button size="lg" disabled={undoHistory.length === 0} onClick={() => {
                setIsEditMode(false);
                setUndoHistory([]);
                navigate('/board-editor');
              }}>
                <Check className="w-5 h-5 mr-2" />
                {tCg("doneEditing")}
              </Button>
            </div>
          )}
        </div>

        {/* Grid with Pagination - 2 rows x 4 cols per page */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Main grid area */}
          <div className="flex-1 flex items-center justify-center overflow-hidden p-2 sm:p-4">
            {hasMultiplePages ? (
              // Match Communication Board pagination style: full-height side arrows
              <div dir="ltr" className="flex items-center w-full h-full gap-3 min-h-0">
                {/* Left pagination arrow - in Hebrew this is "next" */}
                <div className="flex-shrink-0 w-20 h-full flex items-center justify-center">
                  {showLeftArrow ? (
                    <Button
                      data-scan-item=""
                      variant="outline"
                      size="icon"
                      onClick={handleLeftArrowClick}
                      className={cn(
                        "h-full w-16 rounded-2xl border border-border bg-background",
                        "hover:bg-accent hover:text-accent-foreground",
                        "transition-all duration-200"
                      )}
                    >
                      <ChevronLeft style={{ width: 48, height: 48 }} className="text-foreground !w-12 !h-12" strokeWidth={3} />
                    </Button>
                  ) : (
                    <div className="w-16" />
                  )}
                </div>

                <div className="flex-1 h-full min-h-0 flex flex-col gap-2 sm:gap-3">
                  {Array.from({ length: rows }, (_, rowIdx) => {
                    const rowStart = rowIdx * cols;
                    const rowItems = paginatedItems.slice(rowStart, rowStart + cols);
                    return (
                      <div
                        key={rowIdx}
                        className="relative flex-1 min-h-0 rounded-2xl"
                      >
                        <div
                          className="w-full h-full grid gap-2 sm:gap-3 min-h-0"
                          style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
                        >
                          {rowItems.map((item, colIdx) => {
                            const index = rowStart + colIdx;
                            const actualIndex = currentPage * ITEMS_PER_PAGE + index;
                            return (
                              <div
                                key={item.id}
                                data-scan-item
                                draggable={isEditMode}
                                onDragStart={isEditMode ? () => handleDragStart(actualIndex) : undefined}
                                onDragOver={isEditMode ? (e) => handleDragOver(e, actualIndex) : undefined}
                                onDragEnd={isEditMode ? handleDragEnd : undefined}
                                onClick={() => handleItemClick(item)}
                                data-design-id={`emergency-cell-${item.id}`}
                                className={cn(
                                  "relative flex flex-col items-center justify-center gap-1",
                                  "p-2 sm:p-3 lg:p-4 rounded-2xl",
                                  "transition-all duration-200 hover:scale-[1.02] active:scale-95",
                                  "h-full w-full font-bold cursor-pointer min-h-0",
                                  "bg-card border border-[#1E888F]",
                                  "hover:border-primary/50",
                                  isEditMode && "cursor-move",
                                  isEditMode && draggedIndex === actualIndex && "opacity-50 scale-95",
                                )}
                              >
                                {isEditMode && (
                                  <>
                                    <div className="absolute top-1 left-1/2 -translate-x-1/2">
                                      <GripVertical className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                                    </div>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleItemClick(item); }}
                                      className={cn("absolute top-2 p-1.5 sm:p-2 bg-primary rounded-full hover:bg-primary/80", isCgRTL ? "right-2" : "left-2")}
                                    >
                                      <Pencil className="w-4 h-4 sm:w-5 sm:h-5 text-primary-foreground" />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }}
                                      className={cn("absolute top-2 p-1.5 sm:p-2 bg-destructive rounded-full hover:bg-destructive/80", isCgRTL ? "left-2" : "right-2")}
                                    >
                                      <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-destructive-foreground" />
                                    </button>
                                  </>
                                )}
                                <div className="relative z-10 flex items-center justify-center">
                                  {item.iconName && item.iconMode !== "emoji"
                                    ? (() => { const IC = icons[item.iconName as keyof typeof icons]; return IC ? <IC className="text-foreground" style={{ width: VISUAL_SIZE, height: VISUAL_SIZE }} /> : <span style={{ fontSize: VISUAL_SIZE }}>❓</span>; })()
                                    : item.emoji?.startsWith("data:image")
                                      ? <img src={item.emoji} alt="" className="object-contain" style={{ width: VISUAL_SIZE, height: VISUAL_SIZE }} />
                                      : <span style={{ fontSize: VISUAL_SIZE }}>{item.emoji || "❓"}</span>
                                  }
                                </div>
                                <span className="relative z-10 px-1 pb-1 font-semibold text-center text-foreground line-clamp-2 leading-tight" style={{ fontSize: LABEL_SIZE }}>
                                  {getItemLabel(item)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Right pagination arrow - in Hebrew this is "prev" */}
                <div className="flex-shrink-0 w-20 h-full flex items-center justify-center">
                  {showRightArrow ? (
                    <Button
                      data-scan-item
                      variant="outline"
                      size="icon"
                      onClick={handleRightArrowClick}
                      className={cn(
                        "h-full w-16 rounded-2xl border border-border bg-background",
                        "hover:bg-accent hover:text-accent-foreground",
                        "transition-all duration-200"
                      )}
                    >
                      <ChevronRight style={{ width: 48, height: 48 }} className="text-foreground !w-12 !h-12" strokeWidth={3} />
                    </Button>
                  ) : (
                    <div className="w-16" />
                  )}
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex flex-col gap-2 sm:gap-3 min-h-0">
                {Array.from({ length: rows }, (_, rowIdx) => {
                  const rowStart = rowIdx * cols;
                  const rowItems = paginatedItems.slice(rowStart, rowStart + cols);
                  return (
                    <div
                      key={rowIdx}
                      className="relative flex-1 min-h-0 rounded-2xl"
                    >
                      <div
                        className="w-full h-full grid gap-2 sm:gap-3 min-h-0"
                        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
                      >
                        {rowItems.map((item, colIdx) => {
                          const index = rowStart + colIdx;
                          const actualIndex = currentPage * ITEMS_PER_PAGE + index;
                          return (
                            <div
                              key={item.id}
                              data-scan-item
                              draggable={isEditMode}
                              onDragStart={isEditMode ? () => handleDragStart(actualIndex) : undefined}
                              onDragOver={isEditMode ? (e) => handleDragOver(e, actualIndex) : undefined}
                              onDragEnd={isEditMode ? handleDragEnd : undefined}
                              onClick={() => handleItemClick(item)}
                              data-design-id={`emergency-cell-${item.id}`}
                              className={cn(
                                "relative flex flex-col items-center justify-center gap-1",
                                "p-2 sm:p-3 lg:p-4 rounded-2xl",
                                "transition-all duration-200 hover:scale-[1.02] active:scale-95",
                                "h-full w-full font-bold cursor-pointer min-h-0",
                                "bg-card border border-[#1E888F]",
                                "hover:border-primary/50",
                                isEditMode && "cursor-move",
                                isEditMode && draggedIndex === actualIndex && "opacity-50 scale-95",
                              )}
                            >
                              {isEditMode && (
                                <>
                                  <div className="absolute top-1 left-1/2 -translate-x-1/2">
                                    <GripVertical className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                                  </div>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleItemClick(item); }}
                                    className={cn("absolute top-2 p-1.5 sm:p-2 bg-primary rounded-full hover:bg-primary/80", isCgRTL ? "right-2" : "left-2")}
                                  >
                                    <Pencil className="w-4 h-4 sm:w-5 sm:h-5 text-primary-foreground" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }}
                                    className={cn("absolute top-2 p-1.5 sm:p-2 bg-destructive rounded-full hover:bg-destructive/80", isCgRTL ? "left-2" : "right-2")}
                                  >
                                    <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-destructive-foreground" />
                                  </button>
                                </>
                              )}
                              <div className="relative z-10 min-h-0 w-full flex items-center justify-center">
                                {item.iconName && item.iconMode !== "emoji"
                                  ? (() => { const IC = icons[item.iconName as keyof typeof icons]; return IC ? <IC className="text-foreground" style={{ width: VISUAL_SIZE, height: VISUAL_SIZE }} /> : <span style={{ fontSize: VISUAL_SIZE }}>❓</span>; })()
                                  : item.emoji?.startsWith("data:image")
                                    ? <img src={item.emoji} alt="" className="object-contain" style={{ width: VISUAL_SIZE, height: VISUAL_SIZE }} />
                                    : <span style={{ fontSize: VISUAL_SIZE }}>{item.emoji || "❓"}</span>
                                }
                              </div>
                              <span className="relative z-10 px-1 pb-1 font-semibold text-center text-foreground line-clamp-2 leading-tight" style={{ fontSize: LABEL_SIZE }}>
                                {getItemLabel(item)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}